import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useBookEdit } from '@/module/Author/Book/model/BookQuery';
import { BookSchema, bookSchema, defaultBookValues } from '@/module/Author/Book/model/BookSchema';
import { defaultIdentifiers } from '@/module/Author/Publication/model/PublicationTypes';

import { useAppNotification } from '@feature/utils';

import { BookForm } from './BookForm';
import { Book } from './model/BookTypes';

interface BookEditProps {
  book: Book;
}

const convert = (book: Book): BookSchema => ({
  ...book,
  identifiers: book.identifiers ?? defaultIdentifiers,
});

export function BookEdit(props: BookEditProps) {
  const { book } = props;
  const { t } = useTranslation();
  const { mutate, isPending, isError, isSuccess } = useBookEdit(book.id);
  const notification = useAppNotification();

  const onSubmit = (values: BookSchema) => {
    mutate(values, {
      onError: notification.error,
      onSuccess: notification.success,
    });
  };

  const values = useMemo(() => convert(book), [book]);
  const navigate = useNavigate();

  return (
    <BookForm
      title={t('books.form.title.edit')}
      schema={bookSchema}
      defaultValues={defaultBookValues}
      values={values}
      onSubmit={onSubmit}
      isError={isError}
      isSuccess={isSuccess}
      isSubmitting={isPending}
      onCancel={() => navigate({ to: '..' })}
      showWosFields={false}
      showScopusFields={book.source.type === 'SCOPUS'}
    />
  );
}
